import { FormEvent, useCallback, useRef, useState } from 'react'
import { Paperclip, Send } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import MacroPicker, { Macro } from '@/components/MacroPicker'
import { interpolateVariables } from '@/lib/interpolateVariables'
import ChatEditor, { ChatEditorRef } from '@/components/editor/ChatEditor'
import AttachmentPreview from '@/components/AttachmentPreview'
import { useAttachmentInput } from '@/hooks/inbox/useAttachmentInput'
import { getCsrfToken } from './utils'
import type { Agent, EndUser, PendingAttachment } from './types'

export interface MessageComposerProps {
  onSubmit: (e?: FormEvent) => void
  onChange?: (html: string) => void
  onTextChange?: (plainText: string) => void
  placeholder?: string
  disabled?: boolean
  channel?: 'chat' | 'email' | 'whatsapp'

  macros?: Macro[]
  endUser?: EndUser
  currentAgent: Agent
  organizationName?: string | null

  pendingAttachments: PendingAttachment[]
  setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>
  validateFileSize?: (files: File[]) => string | null

  editorRef?: React.RefObject<ChatEditorRef | null>

  extraActions?: React.ReactNode
  sendLabel?: React.ReactNode
  sendDisabled?: boolean
}

export default function MessageComposer({
  onSubmit,
  onChange,
  onTextChange,
  placeholder = 'Digite sua mensagem... (use /nome para macros)',
  disabled = false,
  channel = 'chat',
  macros,
  endUser,
  currentAgent,
  organizationName,
  pendingAttachments,
  setPendingAttachments,
  validateFileSize,
  editorRef: externalEditorRef,
  extraActions,
  sendLabel,
  sendDisabled,
}: MessageComposerProps) {
  const internalEditorRef = useRef<ChatEditorRef>(null)
  const editorRef = externalEditorRef || internalEditorRef

  const [hasDraftText, setHasDraftText] = useState(false)
  const [showMacroPicker, setShowMacroPicker] = useState(false)
  const [macroQuery, setMacroQuery] = useState('')

  // Attachments via shared hook
  const {
    fileInputRef,
    isDragOver,
    handleFileSelect,
    handleRemoveAttachment,
    handlePasteFiles,
    dragHandlers,
  } = useAttachmentInput({
    setPendingAttachments,
    validateFileSize,
  })

  // Text change tracking
  const handleTextChange = useCallback((text: string) => {
    const nextHasText = text.trim().length > 0
    setHasDraftText((prev) => (prev === nextHasText ? prev : nextHasText))
    onTextChange?.(text)
  }, [onTextChange])

  // Macro picker
  const handleMacroTrigger = useCallback((query: string) => {
    setMacroQuery(query)
    setShowMacroPicker(true)
  }, [])

  const closeMacroPicker = useCallback(() => {
    setShowMacroPicker(false)
    setMacroQuery('')
  }, [])

  const handleMacroSelect = useCallback((macro: Macro) => {
    let content = macro.content
    if (endUser) {
      content = interpolateVariables(content, endUser, currentAgent, organizationName)
    }
    editorRef.current?.insertHtml(content)
    closeMacroPicker()
    fetch(`/macros/${macro.id}/use`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': getCsrfToken() },
    }).catch(() => {})
  }, [closeMacroPicker, editorRef, endUser, currentAgent, organizationName])

  const hasMacros = macros && macros.length > 0
  const canSend = (hasDraftText || pendingAttachments.length > 0) && !sendDisabled

  return (
    <div className="relative" {...dragHandlers}>
      {isDragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-blue-50/80 border-2 border-dashed border-blue-400 rounded-xl">
          <span className="text-sm font-medium text-blue-600">Soltar arquivos aqui</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        onChange={handleFileSelect}
        className="hidden"
      />

      {pendingAttachments.length > 0 && (
        <AttachmentPreview
          attachments={pendingAttachments}
          onRemove={handleRemoveAttachment}
        />
      )}

      <div className="relative">
        {showMacroPicker && hasMacros && (
          <MacroPicker
            macros={macros}
            query={macroQuery}
            onSelect={handleMacroSelect}
            onClose={closeMacroPicker}
          />
        )}
      </div>

      <ChatEditor
        ref={editorRef}
        value=""
        onChange={onChange ?? (() => {})}
        onTextChange={handleTextChange}
        onSubmit={onSubmit}
        placeholder={placeholder}
        disabled={disabled}
        channel={channel}
        onMacroTrigger={hasMacros ? handleMacroTrigger : undefined}
        onMacroClose={hasMacros ? closeMacroPicker : undefined}
        onPasteFiles={handlePasteFiles}
      />
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <Paperclip className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Anexar arquivo</TooltipContent>
          </Tooltip>
          {extraActions}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSend}
              className="px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm font-medium"
            >
              {sendLabel || <>Enviar <Send className="w-4 h-4" /></>}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Enviar mensagem</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
